import { useCallback, useEffect, useState } from "react";
import { FilterSidebarInput } from "../../../../components/FilterSidebar/FilterSidebar.types";
import { DynamicListFilterOption } from "./DynamicListFilter.types";

export function useDynamicListFilter<FilterType extends FilterSidebarInput>({
  options,
  initialFilters,
  onApplyFilters,
}: UseDynamicListFilterProps<FilterType>) {
  const [selectedOption, setSelectedOption] = useState<SelectedFilterOption>({
    value: "all",
    parentValue: null,
  });

  const selectOption = useCallback(
    (
      option: DynamicListFilterOption,
      prevOptions: DynamicListFilterOption[] = []
    ) => {
      setSelectedOption({
        value: option.value,
        parentValue: prevOptions[prevOptions.length - 1]?.value ?? null,
      });

      const filterKeys = new Set<string>();
      const collectKeys = (items: DynamicListFilterOption[]) => {
        items.forEach((item) => {
          if (item.filter?.key) filterKeys.add(item.filter.key);
          if (item.children) collectKeys(item.children);
        });
      };
      collectKeys(options);

      const nextFilters = { ...initialFilters };
      filterKeys.forEach((key) => {
        nextFilters[key as keyof FilterType] =
          initialFilters[key as keyof FilterType];
      });

      [...prevOptions, option].forEach(({ filter }) => {
        if (filter) {
          nextFilters[filter.key as keyof FilterType] =
            filter.value as FilterType[keyof FilterType];
        }
      });

      onApplyFilters(nextFilters);
    },
    [initialFilters, onApplyFilters, options]
  );

  useEffect(() => {
    if (selectedOption.value === "all") return;

    const parentOption =
      selectedOption.parentValue === null
        ? undefined
        : options.find((option) => option.value === selectedOption.parentValue);
    const selectedOptionExists =
      selectedOption.parentValue === null
        ? options.some((option) => option.value === selectedOption.value)
        : (parentOption?.children?.some(
            (child) => child.value === selectedOption.value
          ) ?? false);

    if (selectedOptionExists) return;

    if (parentOption) {
      selectOption(parentOption);
      return;
    }

    setSelectedOption({ value: "all", parentValue: null });
    onApplyFilters(initialFilters);
  }, [
    initialFilters,
    onApplyFilters,
    options,
    selectedOption,
    selectOption,
  ]);

  return { selectedOption, selectOption };
}

interface SelectedFilterOption {
  value: string;
  parentValue: string | null;
}

interface UseDynamicListFilterProps<FilterType extends FilterSidebarInput> {
  options: DynamicListFilterOption[];
  filters: FilterType;
  initialFilters: FilterType;
  enableSearchParamsSync?: boolean;
  onApplyFilters: (filters: FilterType, queryUrl?: string) => void;
}
